/**
 * @copyright (C) 2025, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2025-02-04 09:05:39
 **/

import { Environments } from "@rsupport/libraries";

window.AudioContext = window.AudioContext || window.webkitAudioContext;

export default class RNNAudioHandler {
	private readonly _defaultThrouthSize = 94;
	private readonly _vadPrediction = 0.99;
	private readonly _version = "v0.0.2.1";

	private _rnnAudioWorkletNode: AudioWorkletNode | null = null;
	private _microphone: MediaStreamAudioSourceNode | null = null;
	private _audioContext: AudioContext | null = null;
	private _mixedOutput: MediaStreamAudioDestinationNode | null = null;
	private _init: boolean;
	private _isNoiseSuppression: boolean;
	private _isVad: boolean;
	private _vadLimit: number;
	private _throughCount: number;
	private _onVad: ((count: number) => void) | null = null;
	private _is_paused: boolean;
	private _old_audio_stream: MediaStream | null = null;

	constructor(isVad = false, isNoiseSuppression = false, vadLimit = 0.7, throughCount = 1) {
		this._rnnAudioWorkletNode = null;
		this._microphone = null;
		this._audioContext = null;
		this._mixedOutput = null;

		this._init = false;
		this._isVad = isVad;
		this._isNoiseSuppression = isNoiseSuppression;
		this._vadLimit = vadLimit;
		this._throughCount = throughCount * this._defaultThrouthSize;
		this._is_paused = false;

		this._onVad = null;
		this.LOGI(this._version);
	}

	public set onVad(func: (count: number) => void) {
		this._onVad = func;
	}

	LOGI(value: unknown) {
		console.log("#RNNAudioHandler#", value);
	}

	LOGE(value: unknown) {
		console.error("#RNNAudioHandler#", value);
	}

	LOGW(value: unknown) {
		console.warn("#RNNAudioHandler#", value);
	}

	public Pause() {
		this._is_paused = true;
		this.setParam("pause", this._is_paused);
	}

	public Resume() {
		this._is_paused = false;
		this.setParam("pause", this._is_paused);
	}

	public async Stop() {
		try {
			if (this._audioContext) {
				this.LOGI("Stopping...");
				if (this._old_audio_stream) {
					this._old_audio_stream.getAudioTracks()[0].stop();
					this._old_audio_stream = null;
				}

				await this._audioContext.suspend();
				this._microphone && this._microphone.disconnect();
				this._mixedOutput && this._mixedOutput.disconnect();
				this._rnnAudioWorkletNode && this._rnnAudioWorkletNode.disconnect();

				this._microphone = null;
				this._mixedOutput = null;
				this._rnnAudioWorkletNode = null;

				await this._audioContext.close();
				this._audioContext = null;
				this.LOGI("Stopped");
			}
		} catch (e) {
			console.debug("Run error", e);
			alert("Web Audio API is not supported in this browser.");
		}
	}

	public Run(stream: MediaStream, isSelf = false) {
		// eslint-disable-next-line no-async-promise-executor
		return new Promise<MediaStream>(async (resolve, reject) => {
			const workletPath = `${location.host.startsWith("localhost") && Environments.env("UNITY_BUILD", false) ? "/static" : ""}/rnnoise/rnnaudio_worklet_processor.js?t=${Date.now()}`;
			console.debug("#RNNAudioHandler#", "worklet path = " + workletPath);

			try {
				await this.Stop();

				this._audioContext = new AudioContext();
				this._mixedOutput = this._audioContext.createMediaStreamDestination();
				await this._audioContext.audioWorklet.addModule(workletPath);
				if (this._audioContext) {
					this._rnnAudioWorkletNode = new AudioWorkletNode(this._audioContext, "rnnaudio_worklet_processor", {
						processorOptions: {
							kernelBufferSize: 512,
							channelCount: 1,
							vadLimit: this._vadLimit,
							vadPrediction: this._vadPrediction
						}
					});
					this._rnnAudioWorkletNode.port.onmessage = (event: { data: number }) => {
						// Handling data from the node.
						this._onVad?.(event.data);
					};

					this._microphone = this._audioContext.createMediaStreamSource(stream);
					this._microphone.connect(this._rnnAudioWorkletNode).connect(this._mixedOutput);
					if (isSelf) this._rnnAudioWorkletNode.connect(this._audioContext.destination);
					this.initParam();
					this._old_audio_stream = new MediaStream(stream.getAudioTracks());
					stream.removeTrack(stream.getAudioTracks()[0]);
					stream.addTrack(this._mixedOutput.stream.getAudioTracks()[0]);
					resolve(this._mixedOutput.stream);
				}
			} catch (error) {
				this.LOGE(error);
				reject(error);
			}
		});
	}

	public initParam() {
		this.LOGI("init");

		this._init = true;
		this.setParam("vad", this._isVad);
		this.setParam("noiseSuppression", this._isNoiseSuppression);
		this.setParam("vadLimit", this._vadLimit);
		this.setParam("throughCount", this._throughCount);
		this.setParam("pause", this._is_paused);
	}

	public setParam(name: string, value: unknown) {
		if (this._rnnAudioWorkletNode === null || !this._init) {
			this.LOGW("Do not initialize  " + name + ": " + value);
			return;
		}

		const param = this._rnnAudioWorkletNode.parameters.get(name);
		if (!param) {
			this.LOGE("undefined param " + name + ": " + value);
			return;
		}

		param.setValueAtTime(value as number, this._audioContext?.currentTime ?? 0);
		this.LOGI(name + ": " + value);
	}

	public get IsVad() {
		return this._isVad;
	}

	public set IsVad(is) {
		this._isVad = is;
		this.setParam("vad", is);
	}

	public get IsNoiseSuppression() {
		return this._isNoiseSuppression;
	}

	public set IsNoiseSuppression(is) {
		this._isNoiseSuppression = is;
		this.setParam("noiseSuppression", is);
	}

	public get VadLimit() {
		return this._vadLimit;
	}

	public set VadLimit(limit) {
		this._vadLimit = limit;
		this.setParam("vadLimit", limit);
	}

	public get ThroughCount() {
		return this._throughCount;
	}

	public set ThroughCount(count) {
		this._throughCount = count * this._defaultThrouthSize;
		this.setParam("throughCount", count);
	}

	// 옵션과 별개로 CPU를 잡아먹지 않도록 하기 위해 추가
	public get IsPaused() {
		return this._is_paused;
	}
	public set IsPaused(value) {
		if (value) {
			this.Pause();
		} else {
			this.Resume();
		}
	}
}
